import { FileIcon } from '../components/FileIcon'

/**
 * Available file icon extensions in public/assets/file icons/
 * csv, default, doc, docx, html, jpeg, jpg, json, mkv, mp4,
 * pdf, png, ppt, pptx, rar, svg, txt, webp, xls, xlsx, xml, zip
 */

/**
 * Map MIME types and file extensions to icon file names
 */
export function getFileIconExtension(mimeType: string, filename?: string): string {
  const mime = mimeType.toLowerCase()
  const ext = filename ? getFileExtension(filename)?.toLowerCase() : undefined

  // Try exact extension match first (most accurate)
  if (ext) {
    // Check if we have an exact icon for this extension
    const availableIcons = [
      'csv',
      'doc',
      'docx',
      'html',
      'jpeg',
      'jpg',
      'json',
      'mkv',
      'mp4',
      'pdf',
      'png',
      'ppt',
      'pptx',
      'rar',
      'svg',
      'txt',
      'webp',
      'xls',
      'xlsx',
      'xml',
      'zip',
    ]

    if (availableIcons.includes(ext)) {
      return ext
    }

    // Extension-based mappings for common variations
    if (ext === 'gif' || ext === 'bmp' || ext === 'ico') return 'png'
    if (ext === 'avi' || ext === 'mov' || ext === 'wmv' || ext === 'webm' || ext === 'mpeg') return 'mp4'
    if (ext === 'mp3' || ext === 'wav' || ext === 'ogg' || ext === 'flac' || ext === 'aac') return 'mp4' // Use video icon for audio
    if (ext === '7z' || ext === 'tar' || ext === 'gz') return 'zip'
    if (ext === 'js' || ext === 'jsx' || ext === 'ts' || ext === 'tsx' || ext === 'css' || ext === 'scss') return 'html'
    if (ext === 'py' || ext === 'java' || ext === 'php' || ext === 'rb' || ext === 'go' || ext === 'rs') return 'html'
    if (ext === 'md' || ext === 'markdown' || ext === 'log') return 'txt'
  }

  // MIME type-based detection (fallback)

  // Documents
  if (mime.includes('pdf')) return 'pdf'
  if (mime.includes('word') || mime.includes('msword') || mime.includes('wordprocessingml')) {
    return ext === 'docx' ? 'docx' : 'doc'
  }

  // Spreadsheets
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('spreadsheetml')) {
    return ext === 'xlsx' ? 'xlsx' : 'xls'
  }
  if (mime.includes('csv')) return 'csv'

  // Presentations
  if (mime.includes('presentation') || mime.includes('powerpoint')) {
    return ext === 'pptx' ? 'pptx' : 'ppt'
  }

  // Images
  if (mime.includes('image')) {
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg'
    if (mime.includes('png')) return 'png'
    if (mime.includes('webp')) return 'webp'
    if (mime.includes('svg')) return 'svg'
    return 'png' // Default for other images
  }

  // Videos
  if (mime.includes('video')) {
    if (mime.includes('mp4')) return 'mp4'
    if (mime.includes('mkv')) return 'mkv'
    return 'mp4' // Default for other videos
  }

  // Archives
  if (mime.includes('zip')) return 'zip'
  if (mime.includes('rar')) return 'rar'
  if (mime.includes('compress') || mime.includes('archive')) return 'zip'

  // Code/Data
  if (mime.includes('html')) return 'html'
  if (mime.includes('json')) return 'json'
  if (mime.includes('xml')) return 'xml'
  if (mime.includes('text')) return 'txt'

  // Default fallback
  return 'default'
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string | undefined {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1] : undefined
}

/**
 * Render file icon using Untitled UI SVG icons
 */
export function renderFileIcon(
  mimeType: string,
  filename?: string,
  size: number = 32
): JSX.Element {
  const iconExtension = getFileIconExtension(mimeType, filename)

  return <FileIcon extension={iconExtension} size={size} />
}
